/**
 * PricingTeaser
 *
 * Three-column pricing preview on the landing page.
 * Shows name, monthly price, and the 4-5 most compelling features per tier.
 * "Compare all features →" links to the full /pricing page.
 *
 * Uses static prices — the full PricingPage fetches live Stripe prices.
 * If prices change, update both here and in the Stripe dashboard.
 */

import { Link } from 'react-router-dom';

// ─── Tier data ────────────────────────────────────────────────────────────────

const TIERS = [
  {
    name:       'Thoughtful',
    audience:   'For your closest few',
    price:      '$7.99',
    period:     '/mo',
    topBorder:  'border-t-sage',
    highlight:  false,
    badge:      null,
    features: [
      '12 folded cards a year, then $5.99 each',
      'Up to 5 people',
      'Personally written messages',
      'Real printed cards, first-class mail',
      "Birthday, Valentine's, Mother's and Father's Day",
    ],
    ctaStyle:   'border border-navy text-navy hover:bg-navy hover:text-white',
  },
  {
    name:       'Connected',
    audience:   'For family, friends, and your circle',
    price:      '$15.99',
    period:     '/mo',
    topBorder:  'border-t-navy',
    highlight:  true,
    badge:      'Most popular',
    features: [
      '24 folded cards a year, then $5.99 each',
      'Up to 20 people',
      'Personally written messages',
      'Real printed cards, first-class mail',
      'Every occasion type, plus 3 custom',
    ],
    ctaStyle:   'bg-navy text-white hover:bg-opacity-90',
  },
  {
    name:       'Premium',
    audience:   'For everyone in your life',
    price:      '$27.99',
    period:     '/mo',
    topBorder:  'border-t-gold',
    highlight:  false,
    badge:      null,
    features: [
      '48 folded cards a year, then $5.99 each',
      'Unlimited people',
      'Every occasion, unlimited custom',
      '30-day card preview window',
      'Send a card now, no occasion needed',
      'Relationship Wrapped, yearly recap',
    ],
    ctaStyle:   'border border-navy text-navy hover:bg-navy hover:text-white',
  },
] as const;

// ─── PricingTeaser ────────────────────────────────────────────────────────────

export default function PricingTeaser() {
  return (
    <section id="pricing-teaser" className="py-24 bg-white">
      <div className="section-container">

        {/* Heading */}
        <div className="text-center mb-14">
          <p className="font-body text-xs font-semibold tracking-widest text-sage uppercase mb-3">
            Pricing
          </p>
          <h2 className="section-heading text-4xl sm:text-5xl">
            Plans for every relationship
          </h2>
          <p className="mt-4 font-body text-gray-500 text-base max-w-md mx-auto">
            Start with a 14-day free trial. No credit card required,
            cancel or change plans anytime.
          </p>
        </div>

        {/* How pricing works — resets the "how many cards do I get?" question up front */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="rounded-card bg-cream border border-sage/20 px-6 py-5 text-center shadow-warm">
            <p className="font-heading text-lg text-navy mb-1.5">
              Pick your people. We handle the cards.
            </p>
            <p className="font-body text-sm text-gray-600 leading-relaxed">
              Choose a plan by how many people you want to reach. Each one includes a yearly
              allowance of real folded cards (12, 24, or 48), with printing and first-class
              postage in the price. Send past your allowance and extra cards are a flat $5.99,
              billed only when you go over.
            </p>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {TIERS.map((tier) => (
            <div
              key={tier.name}
              className={`relative card flex flex-col p-8 border-t-4 ${tier.topBorder} ${
                tier.highlight ? 'shadow-warm-lg' : 'shadow-warm'
              }`}
            >
              {/* Popular badge */}
              {tier.badge && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="bg-navy text-white font-body text-xs font-semibold px-3.5 py-1 rounded-full whitespace-nowrap">
                    {tier.badge}
                  </span>
                </div>
              )}

              {/* Plan name */}
              <p className="font-body text-xs font-semibold tracking-widest text-gray-400 uppercase mb-1">
                {tier.name}
              </p>

              {/* Who it's for */}
              <p className="font-accent italic text-sage text-sm mb-3">
                {tier.audience}
              </p>

              {/* Price */}
              <div className="flex items-baseline gap-0.5 mb-7">
                <span className="font-heading text-4xl text-navy">{tier.price}</span>
                <span className="font-body text-sm text-gray-400">{tier.period}</span>
              </div>

              {/* Feature list */}
              <ul className="space-y-3 flex-1 mb-8">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <span className="text-sage mt-0.5 flex-shrink-0 font-semibold leading-tight">✓</span>
                    <span className="font-body text-sm text-gray-600 leading-snug">{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={() => document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' })}
                className={`text-center py-3 px-6 rounded-full text-sm font-semibold font-body transition-all duration-200 ${tier.ctaStyle}`}
              >
                Start free trial
              </button>
            </div>
          ))}
        </div>

        {/* Postage note */}
        <div className="text-center mt-8 flex flex-wrap justify-center gap-3">
          <span className="inline-flex items-center gap-2 font-body text-sm text-gray-500 bg-gray-50 border border-gray-100 rounded-full px-5 py-2.5">
            <span>📦</span>
            <span>Printing &amp; first-class postage included in every plan</span>
          </span>
          <span className="inline-flex items-center gap-2 font-body text-sm text-gray-500 bg-gray-50 border border-gray-100 rounded-full px-5 py-2.5">
            <span>💌</span>
            <span>Real folded cards in an envelope, extra cards just $5.99</span>
          </span>
        </div>

        {/* Full pricing link */}
        <div className="text-center mt-6">
          <Link
            to="/pricing"
            className="font-body text-sm text-sage hover:text-navy transition-colors hover:underline"
          >
            Compare all features and annual pricing →
          </Link>
        </div>

      </div>
    </section>
  );
}
